import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardBody, Divider, Button, Chip } from "@nextui-org/react";
import { useGridApiRef } from '@mui/x-data-grid'; 
import useSWR from "swr"; // 1. Tambahkan import SWR

import ClientDataGrid from "../../../../components/elements/ClientDataGrid";
import RootAdmin from "../../../../components/layouts/RootAdmin";
import FilterReactSelect from "../../../../components/elements/FilterReactSelect";
import ModalSKManual from './ModalSKManual';

import useAuth from "../../../../hooks/useAuth";
import { useSelectedCompany } from "../../../../hooks/useSelectedCompany";
import useListCompany from "../../../../hooks/useListCompany";
import { BASE_URL, getFetcher } from "../../../../services/api";

export default function AddSKPage() {
  const apiRef = useGridApiRef();
  const { user } = useAuth(); 
  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const [selectedCompany, setSelectedCompany] = useSelectedCompany();
  const { dataCompany, isLoadingCompany } = useListCompany();

  const hasIdCompanySimpel = selectedCompany?.value || (user?.companies?.length > 0);

  // 2. MEMOIZE OPTIONS: Mencegah infinite loop pada komponen Select
  const companyOptions = useMemo(() => {
    return dataCompany?.data?.map((item) => ({ label: item.name, value: item.id })) || [];
  }, [dataCompany]);

  // 3. GUNAKAN SWR (Tanpa useEffect, tanpa state lokal yang manual)
  // Logic SWR ini diambil persis seperti di IndexPage.js
  const query = selectedCompany?.value
    ? `api/registrasi/find-all/search?companyId=${selectedCompany.value}&returnAll=true`
    : `api/registrasi/find-all/search?companyId=${user?.companies?.map((c) => c?.companyId).join(",")}&returnAll=true`;

  const { data, isLoading, mutate } = useSWR(query, getFetcher);

  const handleOpenAddModal = () => {
    if (selectedCompany?.value) {
      setIsAddModalOpen(true);
    } else {
      alert("Silahkan pilih perusahaan terlebih dahulu.");
    }
  };

  const columns = useMemo(() => [
    { field: "index", headerName: "No" },
    { field: "nomor", headerName: "Nomor Registrasi" },
    { field: "companyName", headerName: "Perusahaan", valueGetter: (value, row) => row?.company?.name },
    { field: "nomorSurat", headerName: "Nomor Surat", valueGetter: (value, row) => row?.nomor_surat_formatted },
    {
      field: "subLayanan",
      headerName: "Sub Layanan",
      valueGetter: (value, row) => {
        let subLayanan = row?.sub_layanan;
        if (subLayanan === "119") return "Registrasi Produsen B3";
        let kategoriBahan = row?.BahanB3Registrasi?.[0]?.kategori_b3;
        return `Registrasi Impor B3 ${kategoriBahan === "Terbatas Dipergunakan" ? "(Terbatas)" : ""}`;
      },
    },
    {
      field: "status_izin",
      headerName: "Status Izin",
      valueGetter: (value, row) => (value || "-"),
    },
    { field: "translatedAsalNegara", headerName: "Asal Negara", valueGetter: (value, row) => row?.BahanB3Registrasi[0]?.translatedAsalNegara },
    { field: "negaraMuat", headerName: "Negara Muat", valueGetter: (value, row) => row?.BahanB3Registrasi[0]?.translatedNegaraMuat },
    { field: "pelabuhanBongkar", headerName: "Pelabuhan Bongkar", valueGetter: (value, row) => row?.BahanB3Registrasi[0]?.translatedPelabuhan },
    { field: "type_registrasi", headerName: "Tipe Registrasi" },
    { field: "tanggal_pengajuan", headerName: "Tanggal Masuk Data", valueGetter: (value) => value },
    {
      field: "status",
      headerName: "Status",
      renderCell: (params) => <Chip size="sm" color="primary" variant="faded" className="capitalize">{params.value}</Chip>,
    },
    { field: "berlaku_dari", headerName: "Berlaku Dari", renderCell: (params) => params?.value ?? "-" },
    { field: "berlaku_sampai", headerName: "Berlaku Sampai", renderCell: (params) => params?.value ?? "-" },
    {
      field: "file_surat",
      headerName: "File SK",
      renderCell: (params) => {
        if (!params?.row?.file_surat) return "Belum Ada File SK";
        return (
          // Sudah memakai BASE_URL agar link tidak ganda
          <a href={`${BASE_URL}/${params.row.file_surat}`} target="_blank" rel="noopener noreferrer" className="text-primary-500 underline">
            Lihat File SK
          </a>
        );
      },
    },
  ], []);

  return (
    <RootAdmin>
      <Card className="w-full mt-3" radius="sm">
        <CardHeader>
          <p className="text-md">Tambah Manual SK (SK Tidak Ditemukan pada Menu Daftar Saya - Update Daftar SK)</p>
        </CardHeader>
        <Divider />
        <CardBody className="w-full h-[550px] p-5">
          <div className="w-full flex justify-between pb-3">
            <div className="w-1/4">
              <FilterReactSelect 
                defaultValue={selectedCompany} 
                isLoading={isLoadingCompany} 
                options={companyOptions} // 4. Panggil opsi yang telah di-memoize
                setValue={setSelectedCompany} 
                placeholder="Pilih Perusahaan" 
              />
            </div>
            
            {hasIdCompanySimpel && (
              <div className="flex items-center">
                <Button size="sm" color="secondary" onPress={handleOpenAddModal}>
                  Tambah Manual SK
                </Button>
              </div>
            )}
          </div>
          
          <ClientDataGrid 
            apiRef={apiRef} 
            columns={columns} 
            isLoading={isLoading} 
            rows={data?.registrasi} 
          />
        </CardBody>
      </Card>

      <ModalSKManual 
        isOpen={isAddModalOpen} 
        onClose={() => setIsAddModalOpen(false)} 
        selectedCompany={selectedCompany}
        onSuccess={() => mutate()} // 5. Cukup panggil mutate() dari SWR untuk refresh tabel
      />
    </RootAdmin>
  );
}